import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './CommunityPage.module.scss'
import WelcomeHeader from '../../WelcomePage/components/WelcomeHeader/WelcomeHeader'
import CommunityService from '../../../service/CommunityService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import SearchBar from '../MoviePage/components/SearchBar/SearchBar'
import { useAuth } from '../../../contexts/AuthContext'
import AuthModal from '../../../components/AuthModal/AuthModal'
import CreatePostModal from './components/CreatePostModal/CreatePostModal'
import CreateChatModal from './components/CreateChatModal/CreateChatModal'
import { PeopleIcon, StarIcon, HeartIcon, EyeIcon, CommentIcon, EditIcon, PlusIcon, LockIcon, PinIcon, ForumIcon, SettingsIcon, GlobeIcon, MicrophoneIcon } from '../../../components/CustomIcons'
import CreateForumModal from './components/CreateForumModal/CreateForumModal'
import CreateThreadModal from './components/CreateThreadModal/CreateThreadModal'
import EmojiPicker from '../../../components/EmojiPicker/EmojiPicker'
import VoiceChat from '../../../components/VoiceChat/VoiceChat'
import { addItemToPanel, getMyPanel } from '../../../service/PersonalPanelService'

const parseRuDate = (dateStr) => {
  if (!dateStr) return null
  const s = String(dateStr)
  // 'DD.MM.YYYY HH:MM' or 'DD.MM.YYYY HH:MM:SS'
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/)
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}`)
  return new Date(s.replace(' ', 'T'))
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Недавно'
  const d = parseRuDate(dateStr)
  if (!d || isNaN(d.getTime())) return String(dateStr)
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const CommunityPage = ({ embedded = false, goBack }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user } = useAuth()

  const getTabFromPath = (path) => {
    if (path.includes('/community/chats')) return 'chats'
    if (path.includes('/community/forums')) return 'forums'
    return 'posts'
  }

  const [posts, setPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [featuredPosts, setFeaturedPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [view, setView] = useState('all') // all, featured, trending
  const [selectedCategory, setSelectedCategory] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('-created_at')
  
  // Новые состояния для расширенной функциональности
  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname)) // posts, chats, forums
  const [chatRooms, setChatRooms] = useState([])
  const [forums, setForums] = useState([])
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [showCreateChat, setShowCreateChat] = useState(false)
  const [showCreateForum, setShowCreateForum] = useState(false)
  const [showCreateThread, setShowCreateThread] = useState(false)
  const [selectedForum, setSelectedForum] = useState(null)
  const [selectedThread, setSelectedThread] = useState(null)
  const [forumThreads, setForumThreads] = useState([])
  const [threadPosts, setThreadPosts] = useState([])
  const [newPostContent, setNewPostContent] = useState('')
  // Chat-specific state
  const [chatSearch, setChatSearch] = useState('')
  const [chatPage, setChatPage] = useState(1)
  const [chatTotalPages, setChatTotalPages] = useState(1)
  const [chatLoading, setChatLoading] = useState(false)
  // Forum search
  const [forumSearch, setForumSearch] = useState('')
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [inviteError, setInviteError] = useState('')
  // Forum thread pagination
  const [forumThreadPage, setForumThreadPage] = useState(1)
  const [forumThreadTotalPages, setForumThreadTotalPages] = useState(1)
  // Post comments state
  const [expandedPostComments, setExpandedPostComments] = useState({}) // { slug: { showing, comments, input, loading } }
  const [postViewCounts, setPostViewCounts] = useState({}) // { slug: count }
  const [panelChatIds, setPanelChatIds] = useState(new Set())
  const [panelForumIds, setPanelForumIds] = useState(new Set())

  useEffect(() => {
    // Первоначальная загрузка только при монтировании
    const initializeData = async () => {
      try {
        if (activeTab === 'posts') {
          await loadData()
        } else {
          // Not a posts tab — clear the posts loading flag immediately
          setLoading(false)
          if (activeTab === 'chats') {
            await loadChatRooms()
          } else if (activeTab === 'forums') {
            await loadForums()
          }
        }
        await loadCategories()
      } catch (error) {
        console.error('Ошибка загрузки данных сообщества:', error)
        setLoading(false)
      }
    }
    initializeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Sync activeTab with URL path
  useEffect(() => {
    const tabFromPath = getTabFromPath(location.pathname)
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Автоматическая перезагрузка при изменении фильтров, поиска или сортировки
  useEffect(() => {
    if (activeTab === 'posts' && view === 'all') {
      const timer = setTimeout(() => {
        loadPosts()
      }, 300) // Дебаунс для поиска
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, sortBy, currentPage, view, activeTab])

  // Chat search debounce
  useEffect(() => {
    if (activeTab === 'chats') {
      const timer = setTimeout(() => {
        loadChatRooms()
      }, 300)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSearch, chatPage, activeTab])

  // Forum search debounce
  useEffect(() => {
    if (activeTab === 'forums' && !selectedForum && !selectedThread) {
      const timer = setTimeout(() => {
        loadForums()
      }, 300)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forumSearch, activeTab])

  useEffect(() => {
    if (!isAuthenticated) return
    loadPanelIds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const loadPanelIds = async () => {
    try {
      const response = await getMyPanel()
      const items = response.data?.items || []
      const chatIds = items.filter((i) => i.item_type === 'chat').map((i) => String(i.item_id))
      const forumIds = items.filter((i) => i.item_type === 'forum').map((i) => String(i.item_id))
      setPanelChatIds(new Set(chatIds))
      setPanelForumIds(new Set(forumIds))
    } catch (error) {
      console.error('Error loading community panel ids:', error)
    }
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (view === 'all') {
        await loadPosts()
      } else if (view === 'featured') {
        await loadFeaturedPosts()
      } else if (view === 'trending') {
        await loadTrendingPosts()
      }
      
      await loadCategories()
    } catch (err) {
      setError('Ошибка загрузки сообщества')
      setRetryAction(() => loadData)
      console.error('Error loading community data:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const checkAuthAndExecute = (action) => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      return false
    }
    return action()
  }
  
  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const openAuthModal = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setShowAuthModal(true)
  }

  const loadPosts = async () => {
    try {
      const params = {
        page: currentPage,
        ordering: sortBy
      }
      
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim()
      }
      
      if (selectedCategory && selectedCategory !== '') {
        params.category = selectedCategory
      }
      
      console.log('Loading posts with params:', params)
      const response = await CommunityService.getCommunityPosts(params)
      setPosts(response.data.results || response.data || [])
      setTotalPages(response.data.total_pages || 1)
    } catch (error) {
      console.error('Error loading posts:', error)
      setError('Не удалось загрузить посты')
    }
  }

  const loadFeaturedPosts = async () => {
    const response = await CommunityService.getFeaturedPosts()
    setFeaturedPosts(response.data || [])
  }

  const loadTrendingPosts = async () => {
    const response = await CommunityService.getTrendingPosts()
    setPosts(response.data || [])
  }

  const loadCategories = async () => {
    try {
      const response = await CommunityService.getCommunityCategories()
      setCategories(response.data || [])
    } catch (err) {
      console.error('Error loading categories:', err)
    }
  }

  // Новые функции для чатов и форумов
  const loadChatRooms = async () => {
    try {
      setChatLoading(true)
      const params = { page: chatPage }
      if (chatSearch && chatSearch.trim()) params.search = chatSearch.trim()
      const response = await CommunityService.getChatRooms(params)
      const data = response.data
      setChatRooms(data.results || data || [])
      setChatTotalPages(data.total_pages || 1)
    } catch (err) {
      console.error('Error loading chat rooms:', err)
    } finally {
      setChatLoading(false)
    }
  }

  const loadForums = async () => {
    try {
      const params = {}
      if (forumSearch && forumSearch.trim()) params.search = forumSearch.trim()
      const response = await CommunityService.getForums(params)
      setForums(response.data || [])
    } catch (err) {
      console.error('Error loading forums:', err)
    }
  }

  const loadForumThreads = async (forumId, page = 1) => {
    try {
      const response = await CommunityService.getForumThreads(forumId, { page })
      const data = response.data
      setForumThreads(data.results || data || [])
      setForumThreadTotalPages(data.total_pages || 1)
      setForumThreadPage(page)
    } catch (err) {
      console.error('Error loading forum threads:', err)
    }
  }

  const handleJoinChatRoom = async (room) => {
    console.log('🚀 Joining chat room:', room);
    if (!isAuthenticated) {
      setShowAuthModal(true)
      return
    }
    
    try {
      await CommunityService.joinChatRoom(room.slug)
      console.log('✅ Successfully joined room, navigating to chat page');
      navigate('/services/community/chat/' + room.slug)
    } catch (err) {
      console.error('❌ Error joining chat room:', err)
      alert('Не удалось присоединиться к чату. Попробуйте еще раз.')
    }
  }

  const handleAddChatToPanel = async (room, e) => {
    e.stopPropagation()
    if (!isAuthenticated) {
      openAuthModal(e)
      return
    }

    try {
      const panelId = String(room.slug)
      await addItemToPanel('chat', panelId, {
        title: room.name,
        description: room.description || '',
        room_type: room.room_type,
        category_name: room.category_name
      })
      setPanelChatIds((prev) => new Set(prev).add(panelId))
    } catch (error) {
      if (error?.response?.status === 400) {
        setPanelChatIds((prev) => new Set(prev).add(String(room.slug)))
      } else {
        console.error('Error adding chat to panel:', error)
      }
    }
  }

  const handleAddForumToPanel = async (forum, e) => {
    e.stopPropagation()
    if (!isAuthenticated) {
      openAuthModal(e)
      return
    }

    try {
      const panelId = String(forum.id)
      await addItemToPanel('forum', panelId, {
        title: forum.name,
        description: forum.description || '',
        category_name: forum.category_name,
        threads_count: forum.threads_count || 0
      })
      setPanelForumIds((prev) => new Set(prev).add(panelId))
    } catch (error) {
      if (error?.response?.status === 400) {
        setPanelForumIds((prev) => new Set(prev).add(String(forum.id)))
      } else {
        console.error('Error adding forum to panel:', error)
      }
    }
  }

  const handleCreatePost = async (postData) => {
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.createCommunityPost(postData)
        setShowCreatePost(false)
        await loadData()
      } catch (err) {
        console.error('Error creating post:', err)
      }
    })
  }

  const handleCreateChatRoom = async (roomData) => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      return null
    }
    const submitData = {
      name: roomData.name,
      description: roomData.description,
      room_type: roomData.room_type,
      category: roomData.category || null,
    }
    const response = await CommunityService.createChatRoom(submitData)
    await loadChatRooms()
    return response.data
  }

  const handleCreateForum = async (forumData) => {
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.createForum(forumData)
        setShowCreateForum(false)
        await loadForums()
      } catch (err) {
        console.error('Error creating forum:', err)
      }
    })
  }

  const handleCreateThread = async (threadData) => {
    checkAuthAndExecute(async () => {
      try {
        if (!selectedForum) return
        await CommunityService.createForumThread(selectedForum.id, threadData)
        setShowCreateThread(false)
        await loadForumThreads(selectedForum.id, 1)
      } catch (err) {
        console.error('Error creating thread:', err)
      }
    })
  }

  const handleCreateForumPost = async (postContent) => {
    checkAuthAndExecute(async () => {
      try {
        if (!selectedThread) return
        await CommunityService.createForumPost(selectedThread.id, { content: postContent })
        setNewPostContent('')
        await loadThreadPosts(selectedThread.id)
      } catch (err) {
        console.error('Error creating forum post:', err)
      }
    })
  }

  const loadThreadPosts = async (threadId) => {
    try {
      const response = await CommunityService.getForumPosts(threadId)
      setThreadPosts(response.data || [])
    } catch (err) {
      console.error('Error loading thread posts:', err)
    }
  }

  const togglePostComments = async (post) => {
    const slug = post.slug
    const current = expandedPostComments[slug]
    if (current?.showing) {
      setExpandedPostComments(prev => ({ ...prev, [slug]: { ...prev[slug], showing: false } }))
      return
    }
    // Track view
    try { await CommunityService.trackPostView(slug) } catch (_) {}
    setPostViewCounts(prev => ({ ...prev, [slug]: (post.views_count || 0) + 1 }))
    // Load comments
    setExpandedPostComments(prev => ({ ...prev, [slug]: { showing: true, comments: current?.comments || [], input: current?.input || '', loading: true } }))
    try {
      const res = await CommunityService.getPostComments(slug)
      const comments = res.data?.results || res.data || []
      setExpandedPostComments(prev => ({ ...prev, [slug]: { ...prev[slug], comments, loading: false } }))
    } catch (_) {
      setExpandedPostComments(prev => ({ ...prev, [slug]: { ...prev[slug], loading: false } }))
    }
  }

  const handlePostCommentSubmit = async (slug) => {
    const entry = expandedPostComments[slug]
    if (!entry?.input?.trim()) return
    try {
      await CommunityService.addPostComment(slug, entry.input.trim())
      setExpandedPostComments(prev => ({ ...prev, [slug]: { ...prev[slug], input: '' } }))
      // Refresh comments
      const res = await CommunityService.getPostComments(slug)
      const comments = res.data?.results || res.data || []
      setExpandedPostComments(prev => ({ ...prev, [slug]: { ...prev[slug], comments } }))
      await loadPosts()
    } catch (err) {
      console.error('Error adding comment:', err)
    }
  }

  const handleTabChange = (tab) => {    navigate(`/services/community/${tab}`)
    setCurrentPage(1)
  }

  const handleJoinByInvite = async () => {
    if (!inviteCode.trim()) {
      setInviteError('Введите код приглашения')
      return
    }
    try {
      const response = await CommunityService.joinByInviteCode(inviteCode.trim().toUpperCase())
      setShowInviteModal(false)
      setInviteCode('')
      setInviteError('')
      const room = response.data.room
      if (room) {
        await loadChatRooms()
        navigate('/services/community/chat/' + room.slug)
      }
    } catch (err) {
      setInviteError(err?.response?.data?.error || 'Неверный код приглашения')
    }
  }

  const handleLikePost = async (post) => {
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.likeCommunityPost(post.slug)
        await loadData()
      } catch (err) {
        console.error('Error liking post:', err)
      }
    })
  }

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue)
    setCurrentPage(1) // Сброс на первую страницу
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    setCurrentPage(1) // Сброс на первую страницу
  }

  if (loading && posts.length === 0 && featuredPosts.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка сообщества...</p>
      </div>
    )
  }

  return (
    <div className={styles.communityPage}>
      {!embedded && <WelcomeHeader />}
      
      <div className={styles.container}>
        {/* <div className={styles.header}>
          <h1 className={styles.title}>
            <span className={styles.icon}><PeopleIcon size={32} /></span>
            Сообщество
          </h1>
          <p className={styles.subtitle}>
            Общение, обсуждения и новости игрового сообщества
          </p>
        </div> */}

        {/* Навигация */}
        <div className={styles.navigation}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.tab} ${activeTab === 'posts' ? styles.active : ''}`}
              onClick={() => handleTabChange('posts')}
            >
              Посты
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'chats' ? styles.active : ''}`}
              onClick={() => handleTabChange('chats')}
            >
              Чаты
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'forums' ? styles.active : ''}`}
              onClick={() => handleTabChange('forums')}
            >
              Форумы
            </button>
          </div>
          
          {isAuthenticated && (
            <div className={styles.createButtons}>
              {activeTab === 'posts' && (
                <button 
                  className={styles.createButton}
                  onClick={() => setShowCreatePost(true)}
                >
                 Создать пост
                </button>
              )}
              {activeTab === 'chats' && (
                <>
                  <button 
                    className={styles.inviteButton}
                    onClick={() => setShowInviteModal(true)}
                  >
                    🔗 Войти по инвайт
                  </button>
                  <button 
                    className={styles.createButton}
                    onClick={() => setShowCreateChat(true)}
                  >
                    Создать чат
                  </button>
                </>
              )}
              {activeTab === 'forums' && (
                <button 
                  className={styles.createButton}
                  onClick={() => setShowCreateForum(true)}
                >
                 Создать форум
                </button>
              )}
            </div>
          )}
        </div>

        {/* Фильтры и поиск */}
        <div className={styles.communityFilters}>
          {activeTab === 'chats' ? (
            <div className={styles.filterGroup}>
              <label>Поиск чатов</label>
              <SearchBar 
                onSearch={(val) => { setChatSearch(val); setChatPage(1) }}
                placeholder="Поиск чатов..."
              />
            </div>
          ) : activeTab === 'forums' ? (
            <div className={styles.filterGroup}>
              <label>Поиск форумов</label>
              <SearchBar
                onSearch={(val) => setForumSearch(val)}
                placeholder="Поиск форумов..."
              />
            </div>
          ) : activeTab === 'posts' ? (
            <>
              <div className={styles.filterGroup}>
                <label>Поиск</label>
                <SearchBar 
                  onSearch={handleSearch}
                  placeholder="Поиск в сообществе..."
                />
              </div>
              
              <div className={styles.filterRow}>
                <div className={styles.filterGroup}>
                  <label>Категория</label>
                  <select 
                    value={selectedCategory} 
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">Все категории</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.slug}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.filterGroup}>
                  <label>Сортировка</label>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="-created_at">Новые сверху</option>
                    <option value="created_at">Старые сверху</option>
                    <option value="-likes_count">По популярности</option>
                    <option value="-comments_count">По комментариям</option>
                    <option value="-views_count">По просмотрам</option>
                    <option value="title">По названию</option>
                  </select>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}><PeopleIcon size={64} /></div>
            <div className={styles.errorContent}>
              <h3 className={styles.errorTitle}>Не удалось загрузить сообщество</h3>
              <p className={styles.errorMessage}>{error}</p>
              <button 
                onClick={handleRetry}
                className={styles.retryButton}
                disabled={loading}
              >
                {loading ? 'Загрузка...' : 'Повторить попытку'}
              </button>
            </div>
          </div>
        )}

        {!isAuthenticated && (
          <div className={styles.authPrompt}>
            <div className={styles.authPromptIcon}><LockIcon size={48} /></div>
            <div className={styles.authPromptContent}>
              <h3>Требуется авторизация</h3>
              <p>Для участия в сообществе необходимо войти в систему</p>
              <button 
                onClick={openAuthModal}
                className={styles.authButton}
              >
                Войти в систему
              </button>
            </div>
          </div>
        )}

        {/* Контент */}
        <div className={styles.content}>
          {activeTab === 'posts' && renderPostsContent()}
          {activeTab === 'chats' && renderChatsContent()}
          {activeTab === 'forums' && renderForumsContent()}
        </div>
      </div>

      {/* Модальное окно авторизации */}
      {showAuthModal && (
        <AuthModal 
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      {/* Модальное окно создания поста */}
      {showCreatePost && (
        <CreatePostModal
          isOpen={showCreatePost}
          onClose={() => setShowCreatePost(false)}
          onSubmit={handleCreatePost}
          categories={categories}
        />
      )}

      {/* Модальное окно создания чата */}
      {showCreateChat && (
        <CreateChatModal
          isOpen={showCreateChat}
          onClose={() => setShowCreateChat(false)}
          onSubmit={handleCreateChatRoom}
          categories={categories}
        />
      )}

      {/* Модальное окно создания форума */}
      {showCreateForum && (
        <CreateForumModal
          isOpen={showCreateForum}
          onClose={() => setShowCreateForum(false)}
          onSubmit={handleCreateForum}
          categories={categories}
        />
      )}

      {/* Модальное окно создания темы */}
      {showCreateThread && (
        <CreateThreadModal
          isOpen={showCreateThread}
          onClose={() => setShowCreateThread(false)}
          onSubmit={handleCreateThread}
          forumName={selectedForum?.name}
        />
      )}

      {/* Модальное окно входа по инвайт-коду */}
      {showInviteModal && (
        <div className={styles.inviteModalOverlay} onClick={() => { setShowInviteModal(false); setInviteCode(''); setInviteError('') }}>
          <div className={styles.inviteModalContent} onClick={e => e.stopPropagation()}>
            <h3>🔗 Войти по инвайт-ссылке</h3>
            <p>Введите код приглашения для вступления в приватный чат</p>
            <input
              className={styles.inviteInput}
              type="text"
              value={inviteCode}
              onChange={e => { setInviteCode(e.target.value.toUpperCase()); setInviteError('') }}
              placeholder="Например: A1B2C3D4"
              maxLength={12}
              onKeyDown={e => e.key === 'Enter' && handleJoinByInvite()}
            />
            {inviteError && <p className={styles.inviteError}>{inviteError}</p>}
            <div className={styles.inviteModalActions}>
              <button className={styles.inviteSubmitBtn} onClick={handleJoinByInvite}>Войти</button>
              <button className={styles.inviteCancelBtn} onClick={() => { setShowInviteModal(false); setInviteCode(''); setInviteError('') }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // Функции рендеринга контента для разных вкладок
  function renderPostsContent() {
    const currentPosts = view === 'featured' ? featuredPosts : posts

    return (
      <>
        <div className={styles.navigation}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.tab} ${view === 'all' ? styles.active : ''}`}
              onClick={() => {
                setView('all')
                loadData()
              }}
            >
              Все посты
            </button>
            <button 
              className={`${styles.tab} ${view === 'featured' ? styles.active : ''}`}
              onClick={() => {
                setView('featured')
                loadFeaturedPosts()
              }}
            >
              Рекомендуемые
            </button>
            <button 
              className={`${styles.tab} ${view === 'trending' ? styles.active : ''}`}
              onClick={() => {
                setView('trending')
                loadTrendingPosts()
              }}
            >
              В тренде
            </button>
          </div>
        </div>

        {currentPosts.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><EditIcon size={64} /></div>
            <h3>Постов не найдено</h3>
            <p>Пока что нет ни одного поста в сообществе</p>
          </div>
        ) : (
          <div className={styles.postsList}>
            {currentPosts.map((post) => {
              const authorAvatarUrl = post.author?.avatar 
                ? (post.author.avatar.startsWith('http') ? post.author.avatar : `https://security.ncore.kg${post.author.avatar}`)
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author?.username || 'User')}&background=E91E63&color=fff&bold=true`
              
              return (
              <div key={post.id} className={styles.postCard}>
                <div className={styles.postHeader}>
                  <div className={styles.authorInfo}>
                    <img 
                      src={authorAvatarUrl} 
                      alt={post.author?.username} 
                      className={styles.authorAvatar}
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author?.username || 'User')}&background=E91E63&color=fff&bold=true`
                      }}
                    />
                    <div className={styles.authorDetails}>
                      <h4 className={styles.authorName}>{post.author?.username}</h4>
                      <span className={styles.postDate}>
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.postMeta}>
                    <span className={styles.category}>{post.category_name}</span>
                    {post.is_pinned && (
                      <span className={styles.pinnedBadge}><PinIcon size={14} style={{ marginRight: '4px' }} /> Закреплено</span>
                    )}
                    {post.is_featured && (
                      <span className={styles.featuredBadge}><StarIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />Рекомендуемый</span>
                    )}
                  </div>
                </div>
                
                <div className={styles.postContent}>
                  <h3 className={styles.postTitle}>{post.title}</h3>
                  <p className={styles.postText}>{post.content}</p>
                  
                  {post.image && (
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className={styles.postImage}
                    />
                  )}
                  
                  {post.video_url && (
                    <div className={styles.videoContainer}>
                      <iframe 
                        src={post.video_url} 
                        title={post.title}
                        frameBorder="0"
                        allowFullScreen
                      />
                    </div>
                  )}
                  
                  {post.external_link && (
                    <a 
                      href={post.external_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.externalLink}
                    >
                      🔗 Перейти по ссылке
                    </a>
                  )}
                </div>

                <div className={styles.postActions}>
                  <button 
                    className={`${styles.actionButton} ${post.is_liked ? styles.liked : ''}`}
                    onClick={() => handleLikePost(post)}
                  >
                    <HeartIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px', fill: post.is_liked ? '#ff5078' : '#888' }} />{post.likes_count || 0}
                  </button>
                  <button
                    className={`${styles.actionButton} ${expandedPostComments[post.slug]?.showing ? styles.liked : ''}`}
                    onClick={(e) => isAuthenticated ? togglePostComments(post) : openAuthModal(e)}
                  >
                    <CommentIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{post.comments_count || 0}
                  </button>

                </div>

                {/* Inline comments section */}
                {expandedPostComments[post.slug]?.showing && (
                  <div className={styles.inlineComments}>
                    {expandedPostComments[post.slug]?.loading ? (
                      <div className={styles.commentsLoading}><Loader size="small" /></div>
                    ) : expandedPostComments[post.slug]?.comments?.length > 0 ? (
                      <div className={styles.commentsList}>
                        {expandedPostComments[post.slug].comments.map((c) => {
                          const cAv = c.author?.avatar
                            ? (c.author.avatar.startsWith('http') ? c.author.avatar : `https://security.ncore.kg${c.author.avatar}`)
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(c.author?.username || 'U')}&background=E91E63&color=fff&bold=true&size=32`
                          return (
                            <div key={c.id} className={styles.commentItem}>
                              <img src={cAv} alt={c.author?.username} className={styles.commentAvatar}
                                onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(c.author?.username || 'U')}&background=E91E63&color=fff&bold=true&size=32` }} />
                              <div className={styles.commentBody}>
                                <span className={styles.commentAuthor}>{c.author?.username}</span>
                                <span className={styles.commentDate}>{new Date(c.created_at).toLocaleDateString('ru-RU')}</span>
                                <p className={styles.commentText}>{c.content}</p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className={styles.noComments}>Пока нет комментариев. Будьте первым!</p>
                    )}
                    {isAuthenticated && (
                      <div className={styles.commentForm}>
                        <textarea
                          className={styles.commentInput}
                          placeholder="Напишите комментарий..."
                          rows={2}
                          value={expandedPostComments[post.slug]?.input || ''}
                          onChange={e => setExpandedPostComments(prev => ({
                            ...prev,
                            [post.slug]: { ...prev[post.slug], input: e.target.value }
                          }))}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePostCommentSubmit(post.slug) } }}
                        />
                        <button
                          className={styles.commentSubmit}
                          onClick={() => handlePostCommentSubmit(post.slug)}
                          disabled={!expandedPostComments[post.slug]?.input?.trim()}
                        >Отправить</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )})}
          </div>
        )}

        {loading && (
          <div className={styles.loadingMore}>
            <Loader size="medium" />
          </div>
        )}

        {totalPages > 1 && view === 'all' && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              setCurrentPage(page)
              if (page !== currentPage) {
                loadData()
              }
            }}
          />
        )}
      </>
    )
  }

  function renderChatsContent() {
    return (
      <div className={styles.chatsContainer}>
        {chatLoading && chatRooms.length === 0 ? (
          <div className={styles.loadingMore}><Loader size="medium" /></div>
        ) : chatRooms.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><CommentIcon size={64} /></div>
            <h3>Чатов не найдено</h3>
            <p>{chatSearch ? `По запросу "${chatSearch}" чатов не найдено` : 'Пока что нет ни одного чата'}</p>
            {isAuthenticated && (
              <button className={styles.createButton} onClick={() => setShowCreateChat(true)}>
                <PlusIcon size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Создать первый чат
              </button>
            )}
          </div>
        ) : (
          <div className={styles.chatRoomsGrid}>
            {chatRooms.map((room) => (
              <div
                key={room.id}
                className={`${styles.chatRoomCard} ${styles[room.room_type]}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!isAuthenticated) { openAuthModal(); return }
                  if (room.is_member) { navigate('/services/community/chat/' + room.slug); return }
                  if (room.room_type === 'private' || room.room_type === 'voice_private') { setShowInviteModal(true); return }
                  handleJoinChatRoom(room)
                }}
              >
                <div className={styles.chatRoomTop}>
                  <div className={styles.chatRoomHeader}>
                    <span className={styles.roomIcon}>
                      {room.room_type === 'public' ? <GlobeIcon size={24} /> :
                       room.room_type === 'private' ? <LockIcon size={24} /> :
                       room.room_type === 'voice_private' ? <MicrophoneIcon size={24} /> :
                       <MicrophoneIcon size={24} />}
                    </span>
                    <div className={styles.roomInfo}>
                      <h4>{room.name}</h4>
                      <div className={styles.roomBadges}>
                        <span className={`${styles.roomType} ${styles[room.room_type]}`}>
                          {room.room_type === 'public' ? 'Публичный' :
                           room.room_type === 'private' ? 'Приватный' :
                           room.room_type === 'voice_private' ? 'Прив. голосовой' : 'Голосовой'}
                        </span>
                        {room.is_member && <span className={styles.memberBadge}>✓ Вы участник</span>}
                        {room.is_active && <span className={styles.activeBadge}>Активен</span>}
                      </div>
                    </div>
                  </div>
                  <p className={styles.chatRoomDescription}>{room.description}</p>
                </div>
                <div className={styles.chatRoomBottom}>
                  <div className={styles.chatRoomMeta}>
                    <span><PeopleIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{room.members_count || 0} участников</span>
                    <span>📂 {room.category_name}</span>
                    {room.last_message_at && (
                      <span><CommentIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{formatDate(room.last_message_at)}</span>
                    )}
                  </div>

                  {/* Invite code for private/voice/voice_private rooms owned by user */}
                  {(room.room_type === 'private' || room.room_type === 'voice' || room.room_type === 'voice_private') && room.owner?.id === user?.id && room.invite_code && (
                    <div className={styles.inviteCodeBlock}>
                      <span className={styles.inviteCodeLabel}>Код приглашения:</span>
                      <code className={styles.inviteCode}>{room.invite_code}</code>
                      <button
                        className={styles.copyInviteBtn}
                        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(room.invite_code) }}
                        title="Копировать код"
                      >📋</button>
                    </div>
                  )}

                  <div className={styles.chatRoomActions}>
                    {isAuthenticated ? (
                      <>
                        {room.is_member ? (
                          <button className={styles.openButton} onClick={e => { e.stopPropagation(); navigate('/services/community/chat/' + room.slug) }}>
                            {(room.room_type === 'voice' || room.room_type === 'voice_private') ? (
                              <><MicrophoneIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Войти в голосовой</>  
                            ) : 'Открыть чат'}
                          </button>
                        ) : (room.room_type === 'private' || room.room_type === 'voice_private') ? (
                          <button className={styles.inviteButton} onClick={e => { e.stopPropagation(); setShowInviteModal(true) }}>
                            <LockIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Войти по инвайт
                          </button>
                        ) : (
                          <button className={styles.joinButton} onClick={e => { e.stopPropagation(); handleJoinChatRoom(room) }}>
                            {room.room_type === 'voice' ? (
                              <><MicrophoneIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Подключиться</>
                            ) : 'Присоединиться'}
                          </button>
                        )}
                        {room.owner?.id === user?.id && (
                          <button className={styles.manageButton} title="Управление комнатой" onClick={e => { e.stopPropagation(); navigate('/services/community/chat/' + room.slug) }}>
                            <SettingsIcon size={16} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                            Управление
                          </button>
                        )}
                        <button className={`${styles.panelAddBtn} ${panelChatIds.has(String(room.slug)) ? styles.panelAddBtnAdded : ''}`} onClick={(e) => handleAddChatToPanel(room, e)}>
                          {panelChatIds.has(String(room.slug)) ? 'Уже в панели' : 'В панель'}
                        </button>
                      </>
                    ) : (
                      <button className={styles.authRequiredButton} onClick={e => { e.stopPropagation(); openAuthModal(e) }}>
                        <LockIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Войдите для участия
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chat pagination */}
        {chatTotalPages > 1 && (
          <Pagination
            currentPage={chatPage}
            totalPages={chatTotalPages}
            onPageChange={(page) => setChatPage(page)}
          />
        )}

      </div>
    )
  }

  function renderForumsContent() {
    // Просмотр постов в теме
    if (selectedThread) {
      return (
        <div className={styles.threadView}>
          <div className={styles.threadViewHeader}>
            <button 
              className={styles.backButton}
              onClick={() => {
                setSelectedThread(null)
                setThreadPosts([])
              }}
            >
              ← Назад к темам
            </button>
            <div className={styles.threadInfo}>
              <h3>{selectedThread.title}</h3>
              <div className={styles.threadMeta}>
                <span>👤 Автор: {selectedThread.user?.username}</span>
                <span><CommentIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{threadPosts.length} ответов</span>
                <span>🕒 {formatDate(selectedThread.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Two-column layout: posts left, voice+chat right */}
          <div className={styles.threadLayout}>

            {/* LEFT: posts + reply */}
            <div className={styles.threadPostsPanel}>
              <div className={styles.forumPostsList}>
                {threadPosts.length === 0 && (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>💬</div>
                    <h3>Пока нет ответов</h3>
                    <p>Будьте первым, кто ответит в этой теме</p>
                  </div>
                )}
                {threadPosts.map((post, index) => {
                  const avatarUrl = post.user?.avatar
                    ? (post.user.avatar.startsWith('http') ? post.user.avatar : `https://security.ncore.kg${post.user.avatar}`)
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.username || 'User')}&background=E91E63&color=fff&bold=true`

                  return (
                    <div key={post.id} className={`${styles.forumPost} ${post.is_solution ? styles.forumPostSolution : ''}`}>
                      <div className={styles.forumPostLeft}>
                        <img
                          src={avatarUrl}
                          alt={post.user?.username}
                          className={styles.forumPostAvatar}
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.username || 'User')}&background=E91E63&color=fff&bold=true`
                          }}
                        />
                        <div className={styles.forumPostThread} />
                        <span className={styles.forumPostNumber}>#{index + 1}</span>
                      </div>
                      <div className={styles.forumPostRight}>
                        <div className={styles.forumPostHeader}>
                          <div className={styles.forumPostMeta}>
                            <span className={styles.forumPostUsername}>{post.user?.username || 'Unknown'}</span>
                            <span className={styles.forumPostDate}>{formatDate(post.created_at)}</span>
                            {index === 0 && <span className={styles.opBadge}>Автор</span>}
                            {post.is_solution && <span className={styles.solutionBadge}>✓ Решение</span>}
                          </div>
                        </div>
                        <div className={styles.forumPostBody}>
                          {post.content}
                          {post.is_edited && (
                            <span className={styles.editedLabel}> (ред. {formatDate(post.edited_at)})</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {isAuthenticated && !selectedThread.is_locked && (
                <div className={styles.replyForm}>
                  <textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder="Напишите ответ..."
                    rows={2}
                    className={styles.replyTextarea}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCreateForumPost(newPostContent) } }}
                  />
                  <div className={styles.replyActions}>
                    <EmojiPicker onSelect={(emoji) => setNewPostContent(prev => prev + emoji)} />
                    <button
                      onClick={() => handleCreateForumPost(newPostContent)}
                      className={styles.replyButton}
                      disabled={!newPostContent.trim()}
                    >
                      Отправить
                    </button>
                  </div>
                </div>
              )}

              {!isAuthenticated && (
                <div className={styles.replyForm}>
                  <button className={styles.authRequiredButton} onClick={openAuthModal}>
                    <LockIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Войдите, чтобы ответить
                  </button>
                </div>
              )}

              {selectedThread.is_locked && (
                <div className={styles.lockedNotice}>
                  <LockIcon size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Эта тема заблокирована и недоступна для новых ответов
                </div>
              )}
            </div>

            {/* RIGHT: Voice Chat + Text Chat */}
            <div className={styles.threadVoiceChatPanel}>
              <div className={styles.threadVoiceChatLabel}>
                <MicrophoneIcon size={13} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Голосовой чат темы
              </div>
              <div className={styles.threadVoiceChatInner}>
                <VoiceChat
                  roomSlug={`ft-${selectedThread.id}`}
                  currentUser={user}
                  isOwner={false}
                  members={[]}
                />
              </div>
            </div>

          </div>
        </div>
      )
    }

    // Список тем в форуме
    if (selectedForum) {
      return (
        <div className={styles.forumView}>
          <div className={styles.forumViewHeader}>
            <button 
              className={styles.backButton}
              onClick={() => {
                setSelectedForum(null)
                setForumThreads([])
                setForumThreadPage(1)
                setForumThreadTotalPages(1)
              }}
            >
              ← Назад к форумам
            </button>
            <div className={styles.forumInfo}>
              <h3><ForumIcon size={24} style={{ marginRight: '8px' }} /> {selectedForum.name}</h3>
              <p>{selectedForum.description}</p>
            </div>
            {isAuthenticated && (
              <button
                className={styles.createButton}
                onClick={() => setShowCreateThread(true)}
              >
                📝 Создать тему
              </button>
            )}
          </div>
          
          <div className={styles.threadsList}>
            {forumThreads.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📄</div>
                <h3>Тем не найдено</h3>
                <p>В этом форуме пока нет тем для обсуждения</p>
                {isAuthenticated && (
                  <button
                    className={styles.createButton}
                    onClick={() => setShowCreateThread(true)}
                  >
                    📝 Создать первую тему
                  </button>
                )}
              </div>
            ) : (
              forumThreads.map((thread) => {
                const authorAvatar = thread.user?.avatar 
                  ? (thread.user.avatar.startsWith('http') 
                      ? thread.user.avatar 
                      : `https://security.ncore.kg${thread.user.avatar}`)
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(thread.user?.username || 'User')}&background=E91E63&color=fff&bold=true`

                return (
                <div 
                  key={thread.id} 
                  className={`${styles.threadCard} ${thread.is_pinned ? styles.pinned : ''}`}
                  onClick={() => navigate(`/services/community/forum/${selectedForum.id}/thread/${thread.id}`)}
                >
                  <div className={styles.threadHeader}>
                    <div className={styles.threadTitleSection}>
                      <h4>{thread.title}</h4>
                      <div className={styles.threadBadges}>
                        {thread.is_pinned && <span className={styles.pinnedBadge}><PinIcon size={14} style={{ marginRight: '4px' }} /> Закреплено</span>}
                        {thread.is_locked && <span className={styles.lockedBadge}><LockIcon size={14} style={{ marginRight: '4px' }} /> Заблокировано</span>}
                        {thread.is_solved && <span className={styles.solvedBadge}>✓ Решено</span>}
                      </div>
                    </div>
                  </div>
                  <div className={styles.threadMeta}>
                    <div className={styles.threadAuthor}>
                      <img 
                        src={authorAvatar}
                        alt={thread.user?.username}
                        className={styles.threadAuthorAvatar}
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(thread.user?.username || 'User')}&background=E91E63&color=fff&bold=true`
                        }}
                      />
                      <span className={styles.threadAuthorName}>{thread.user?.username || 'Неизвестно'}</span>
                    </div>
                    <span><CommentIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{thread.posts_count || 0} ответов</span>
                    <span>🕒 {formatDate(thread.created_at)}</span>
                  </div>
                </div>
              )})
            )}
          </div>
        </div>
      )
    }

    // Список форумов
    return (
      <div className={styles.forumsContainer}>
        <div className={styles.forumsGrid}>
          {forums.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><ForumIcon size={48} /></div>
              <h3>Форумов не найдено</h3>
              <p>Пока что нет ни одного форума в сообществе</p>
              {isAuthenticated && (
                <button
                  className={styles.createButton}
                  onClick={() => setShowCreateForum(true)}
                >
                  <ForumIcon size={18} style={{ marginRight: '6px' }} /> Создать первый форум
                </button>
              )}
            </div>
          ) : (
            forums.map((forum) => (
              <div
                key={forum.id}
                className={styles.forumCard}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!isAuthenticated) { openAuthModal(); return }
                  navigate('/services/community/forum/' + forum.id)
                }}
              >
                <div className={styles.forumCardHeader}>
                  <div className={styles.forumIcon}><ForumIcon size={32} /></div>
                  <div className={styles.forumCardInfo}>
                    <h4>{forum.name}</h4>
                    <span className={styles.categoryBadge}>{forum.category_name}</span>
                    {forum.is_private && <span className={styles.privateBadge}><LockIcon size={14} style={{ marginRight: '4px' }} /> Приватный</span>}
                  </div>
                </div>
                <p className={styles.forumDescription}>{forum.description}</p>
                <div className={styles.forumStats}>
                  <span>📄 {forum.threads_count || 0} тем</span>
                </div>
                <div className={styles.forumActions}>
                  <button 
                    className={styles.enterButton}
                    onClick={e => { e.stopPropagation(); navigate('/services/community/forum/' + forum.id) }}
                  >
                    Войти в форум →
                  </button>
                  <button
                    className={`${styles.panelAddBtn} ${panelForumIds.has(String(forum.id)) ? styles.panelAddBtnAdded : ''}`}
                    onClick={(e) => handleAddForumToPanel(forum, e)}
                  >
                    {panelForumIds.has(String(forum.id)) ? 'Уже в панели' : 'В панель'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    )
  }
}

export default CommunityPage